import qs from 'qs'
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import { useProjectStore } from '@/hooks/use-project-store'
import { fetcher } from '@/lib/utils'
import { useCallback, useMemo, useState } from 'react'
import useSWR from 'swr'
import { CoreView } from './CoreView'
import type { PostHogInsightResult } from '@/lib/postHog'
import type { CoreMetrics, DateFrom, MetricType } from './types'
import { zipWith } from 'lodash'
import { PostHogProvider } from './context'
import { type BaseOption, SelectV2 } from '@/components/ui/select-v2'
import { createDateOptions } from './consts'
import { BreakdownViews } from './break-down'
import { Spin } from '@/components/ui/spin'
import { dayjs } from '@/utils/dayjs'
import { useTranslations } from 'next-intl'
import { X } from 'lucide-react'

export function AnalyticsModal() {
  const { project } = useProjectStore()
  const t = useTranslations('analytics')

  const [dateFrom, setDateFrom] = useState<DateFrom>(7)
  const [activeMetricType, setActiveMetricType] =
    useState<MetricType>('visitors')

  const insightsUrl = useMemo(() => {
    const queryObj = {
      date_from: dayjs.utc().subtract(dateFrom, 'day').toISOString(),
      aggregation: ['dau', 'total'],
    }

    const queryString = qs.stringify(queryObj, { arrayFormat: 'repeat' })

    return `/api/stats/projects/${project?._id.toString()}/insights?${queryString}`
  }, [project, dateFrom])

  const { data: insights, isLoading } = useSWR<PostHogInsightResult>(
    insightsUrl,
    fetcher,
  )

  const buildChartData = useCallback((labels: string[], data: number[]) => {
    return zipWith(labels, data, (label, value) => ({
      date: label,
      value: value,
    }))
  }, [])

  const coreMetrics = useMemo<CoreMetrics | undefined>(() => {
    if (!insights?.data) return undefined
    const insightData = insights.data

    const uv = insightData?.[0]?.result?.[0] || {}
    const pv = insightData?.[1]?.result?.[0] || {}

    const {
      count: uvCount,
      action: { days: uvDays },
      data: uvData,
    } = uv

    const uvLabels = uvDays.map((d) => {
      return dayjs.utc(d).local().format('DD-MM')
    })

    const {
      count: pvCount,
      action: { days: pvDays },
      data: pvData,
    } = pv

    const pvLabels = pvDays.map((d) => {
      return dayjs.utc(d).local().format('DD-MM')
    })

    return {
      visitors: uvCount,
      pageviews: pvCount,
      datasets: {
        visitors: buildChartData(uvLabels, uvData),
        pageviews: buildChartData(pvLabels, pvData),
      },
    }
  }, [insights, buildChartData])

  const handleChangeDateFrom = useCallback((value?: string | number) => {
    setDateFrom(value as DateFrom)
  }, [])

  const dateOptions = useMemo(() => createDateOptions(t), [t])

  return (
    <PostHogProvider>
      <DialogContent
        noCloseButton
        className="!rounded-none dark:border-transparent p-0"
        overlayClassName="bg-black"
      >
        <div className="max-h-[100dvh] sm:max-h-[100vh] p-6 overflow-auto flex flex-col relative">
          <DialogPrimitive.Close className="absolute right-4 top-4 sm:top-6 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 p-2 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50 z-50 pointer-events-auto shadow-lg">
            <X className="h-5 w-5 text-white" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
          <DialogHeader className="pb-4 border-b border-b-white/10">
            <DialogTitle className="hidden">{t('label')}</DialogTitle>
            <input className="sr-only" autoFocus /> {/* 用来吸收自动 focus */}
            <div className="flex gap-x-[20px]">
              <div className="text-white text-2xl font-semibold">
                {t('label')}
              </div>
            </div>
            <DialogDescription className="flex justify-between items-center">
              <span className="text-base text-left">{t('description')}</span>
              <SelectV2
                modal
                value={dateFrom}
                options={dateOptions}
                onChange={handleChangeDateFrom}
                buttonProps={{ className: 'border-transparent' }}
              />
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            {isLoading && (
              <div className="absolute inset-0 flex justify-center items-center z-20 bg-black/20 backdrop-blur-sm rounded-lg">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full border-4 border-white/20 border-t-[#FFD700] w-10 h-10"></div>
                  <span className="text-white/80 text-sm">
                    Loading analytics...
                  </span>
                </div>
              </div>
            )}
            <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
              <CoreView
                metrics={coreMetrics}
                activeMetricType={activeMetricType}
                setActiveMetricType={setActiveMetricType}
              />

              <BreakdownViews />
            </div>
          </div>
        </div>
      </DialogContent>
    </PostHogProvider>
  )
}
